import React, { useState, useEffect } from "react";
import Modal from "../Modal/Modal";
import TransporterService from "../../services/TransporterService";
import { useLoader } from "../../contexts/LoaderContext";
import { showToast } from "../Toaster";
import { useAuth } from "../../contexts/AuthContext";
import { Transporter } from "../../interfaces/transporter.interface";

interface TransporterCreateRequest {
  name: string;
  phoneNumber?: string;
  email?: string;
  // Legacy fields for backward compatibility
  contact?: string;
  address?: string;
}

interface TransporterModalProps {
  isOpen: boolean;
  onClose: () => void;
  transporter?: Transporter | null;
  onSuccess: () => void;
}

const TransporterModal: React.FC<TransporterModalProps> = ({
  isOpen,
  onClose,
  transporter,
  onSuccess,
}) => {
  const { showLoader, hideLoader } = useLoader();
  const { userProfile } = useAuth();
  const [formData, setFormData] = useState<TransporterCreateRequest>({
    name: "",
    phoneNumber: "",
    email: "",
    contact: "",
    address: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = !!transporter;

  useEffect(() => {
    if (transporter) {
      setFormData({
        name: transporter.name,
        phoneNumber: transporter.phoneNumber || "",
        email: transporter.email || "",
        // Support legacy contact field - prioritize phoneNumber over legacy contact
        contact: transporter.phoneNumber || transporter.contact || "",
        address: transporter.address || "",
      });
    } else {
      setFormData({
        name: "",
        phoneNumber: "",
        email: "",
        contact: "",
        address: "",
      });
    }
    setErrors({});
  }, [transporter]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required field validation
    if (!formData.name.trim()) {
      newErrors.name = "Transporter name is required";
    }

    // Email validation (only if email is provided)
    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = "Invalid email format";
      }
    }

    // Phone Number validation (optional but if provided, should be valid)
    if (formData.phoneNumber && formData.phoneNumber.trim()) {
      const phoneRegex = /^[+]?[\d\s\-()]{10,}$/;
      if (!phoneRegex.test(formData.phoneNumber)) {
        newErrors.phoneNumber =
          "Please enter a valid phone number (minimum 10 digits)";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async () => {
    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    setIsSubmitting(true);
    showLoader();

    try {
      // Prepare the data for API submission
      const submitData = {
        name: formData.name.trim(),
        phoneNumber: formData.phoneNumber?.trim() || undefined,
        email: formData.email?.trim() || undefined,
        address: formData.address?.trim() || undefined,
      };

      // Remove undefined fields
      Object.keys(submitData).forEach(
        (key) =>
          submitData[key as keyof typeof submitData] === undefined &&
          delete submitData[key as keyof typeof submitData]
      );

      if (isEditMode && transporter) {
        // Check user role to determine which API to call
        if (userProfile?.role?.name === "ROLE_CLIENT_ADMIN") {
          // Enterprise admin - use enterprise transporters endpoint
          await TransporterService.updateTransporter(transporter.id, {
            id: transporter.id,
            ...submitData,
          });
        } else {
          // Federation admin - use federation transporters endpoint
          await TransporterService.updateFederationTransporter(transporter.id, {
            id: transporter.id,
            ...submitData,
          });
        }
        showToast("Transporter updated successfully", "success");
      } else {
        // Check user role to determine which API to call
        if (userProfile?.role?.name === "ROLE_CLIENT_ADMIN") {
          // Enterprise admin - use enterprise transporters endpoint
          await TransporterService.createTransporter(submitData);
        } else {
          // Federation admin - use federation transporters endpoint
          await TransporterService.createFederationTransporter(submitData);
        }
        showToast("Transporter created successfully", "success");
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error saving transporter:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Error saving transporter";
      showToast(errorMessage, "error");
    } finally {
      setIsSubmitting(false);
      hideLoader();
    }
  };

  const getFooterButtons = () => (
    <div className="flex justify-center gap-4">
      <button
        type="button"
        className="secondary_button w-[128px]"
        onClick={onClose}
        disabled={isSubmitting}
      >
        Cancel
      </button>
      <button
        type="button"
        className="primary_button w-[128px]"
        onClick={handleSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting ? "Saving..." : isEditMode ? "Update" : "Create"}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? "Edit Transporter" : "Add Transporter"}
      size="xl"
      closeOnModalOverlayClick={true}
      footer={getFooterButtons()}
    >
      <div className="modal-form">
        {/* 
          Updated Enterprise Transporter fields:
          - Name (required)
          - Phone Number (optional)
          - Email (optional)
          - Address (optional)
        */}
        {/* Read-only Transporter Information (Edit Mode) */}
        {isEditMode && transporter && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
            <h4 className="text-sm font-medium text-gray-700 mb-3">
              Transporter Information
            </h4>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-600">UID:</span>
                <div className="text-gray-800">{transporter.uid}</div>
              </div>
              <div>
                <span className="font-medium text-gray-600">Users:</span>
                <div className="text-gray-800">
                  {transporter.users?.length || 0}
                </div>
              </div>
              {transporter.createdBy && (
                <div>
                  <span className="font-medium text-gray-600">Created By:</span>
                  <div className="text-gray-800">
                    {transporter.createdBy.name}
                  </div>
                </div>
              )}
              {transporter.createDate && (
                <div>
                  <span className="font-medium text-gray-600">
                    Created Date:
                  </span>
                  <div className="text-gray-800">
                    {new Date(transporter.createDate).toLocaleDateString()}
                  </div>
                </div>
              )}
              {transporter.updateDate && (
                <div>
                  <span className="font-medium text-gray-600">
                    Last Updated:
                  </span>
                  <div className="text-gray-800">
                    {new Date(transporter.updateDate).toLocaleDateString()}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Transporter Name */}
          <div className="form-group lg:col-span-2">
            <label className="form-label required_field_label">
              Transporter Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter transporter name"
              className={`form-input ${errors.name ? "error" : ""}`}
            />
            {errors.name && (
              <div className="form-help error">{errors.name}</div>
            )}
          </div>

          {/* Phone Number */}
          <div className="form-group">
            <label className="form-label field_label">Phone Number</label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              placeholder="Enter phone number"
              className={`form-input ${errors.phoneNumber ? "error" : ""}`}
            />
            {errors.phoneNumber && (
              <div className="form-help error">{errors.phoneNumber}</div>
            )}
          </div>

          {/* Email */}
          <div className="form-group">
            <label className="form-label field_label">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter email address"
              className={`form-input ${errors.email ? "error" : ""}`}
            />
            {errors.email && (
              <div className="form-help error">{errors.email}</div>
            )}
          </div>

          {/* Address */}
          <div className="form-group lg:col-span-2">
            <label className="form-label field_label">Address</label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Enter address"
              rows={3}
              className={`form-input ${errors.address ? "error" : ""}`}
            />
            {errors.address && (
              <div className="form-help error">{errors.address}</div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TransporterModal;
